import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Select, Chip } from 'common/components';
import { Accordion } from 'components/Accordion/Accordion';
import { useAuth } from 'contexts/AuthContext';
import { useGetAccessManager, useTenantConfig } from 'services/hooks/queries';
import type { SelectOption, ChipColor } from 'common/components';
import styles from './TenantConfigViewer.module.scss';

const SECTION_KEYS = [
    { key: 'status', label: 'Status' },
    { key: 'twilioCredentials', label: 'Twilio Credentials' },
    { key: 'businessHours', label: 'Business Hours' },
    { key: 'staleCallsConfig', label: 'Stale Calls Config' },
    { key: 'dashboardData', label: 'Dashboard Data' },
    { key: 'crmCredentials', label: 'CRM Credentials' },
] as const;

function isMasked(value: string): boolean {
    return value.includes('••••');
}

function isEmpty(value: unknown): boolean {
    if (value === undefined || value === null) return true;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length === 0) return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
}

function isConfigured(value: unknown): boolean {
    if (value === undefined || value === null || value === '') return false;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length === 0) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
}

const CREDENTIAL_SECTIONS = new Set(['twilioCredentials', 'crmCredentials']);

function renderValue(value: unknown): React.ReactNode {
    if (value === undefined || value === null) {
        return <span>—</span>;
    }

    if (typeof value === 'string') {
        if (isMasked(value)) {
            return <span className={styles.maskedValue}>{value}</span>;
        }
        return <span>{value}</span>;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return <span>{String(value)}</span>;
    }

    if (Array.isArray(value)) {
        const hasObjects = value.some((v) => typeof v === 'object' && v !== null);
        if (hasObjects) {
            return (
                <div className={styles.nestedObject}>
                    {value.map((item, i) => (
                        <div key={i} className={styles.nestedRow}>
                            <span className={styles.key}>[{i}]</span>
                            <span className={styles.value}>{renderValue(item)}</span>
                        </div>
                    ))}
                </div>
            );
        }
        return <span>{value.join(', ')}</span>;
    }

    if (typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        return (
            <div className={styles.nestedObject}>
                {Object.entries(obj).map(([k, v]) => (
                    <div key={k} className={styles.nestedRow}>
                        <span className={styles.key}>{k}</span>
                        <span className={styles.value}>{renderValue(v)}</span>
                    </div>
                ))}
            </div>
        );
    }

    return <span>{String(value)}</span>;
}

function SectionBody({ sectionKey, data }: { sectionKey: string; data: unknown }) {
    const showIndicators = CREDENTIAL_SECTIONS.has(sectionKey);

    if (isEmpty(data)) {
        return <p className={styles.notConfigured}>Not configured</p>;
    }

    if (sectionKey === 'status') {
        return <span className={styles.statusText}>{String(data)}</span>;
    }

    if (typeof data === 'object' && !Array.isArray(data)) {
        const obj = data as Record<string, unknown>;
        return (
            <div className={styles.sectionContent}>
                {Object.entries(obj).map(([k, v]) => {
                    const isNested = v !== null && typeof v === 'object';
                    if (isNested) {
                        return (
                            <div key={k} className={styles.nestedSection}>
                                <div className={showIndicators ? styles.credentialRow : styles.row}>
                                    <span className={styles.key}>{k}</span>
                                    <span className={styles.value} />
                                    {showIndicators && (
                                        <span className={isConfigured(v) ? styles.configured : styles.notSet}>
                                            {isConfigured(v) ? 'Configured' : 'Not set'}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.nestedBody}>{renderValue(v)}</div>
                            </div>
                        );
                    }
                    return (
                        <div key={k} className={showIndicators ? styles.credentialRow : styles.row}>
                            <span className={styles.key}>{k}</span>
                            <span className={styles.value}>{renderValue(v)}</span>
                            {showIndicators && (
                                <span className={isConfigured(v) ? styles.configured : styles.notSet}>
                                    {isConfigured(v) ? 'Configured' : 'Not set'}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    return <div className={styles.sectionContent}>{renderValue(data)}</div>;
}

function statusChipColor(status: string | undefined): ChipColor {
    if (status === 'ACTIVE') return 'success';
    if (status === 'INACTIVE') return 'error';
    return 'warning';
}

const TenantConfigViewer: React.FC = () => {
    const [selectedTenantId, setSelectedTenantId] = useState('');
    const { userRoles } = useAuth();

    const isSuperAdmin = userRoles
        .map((r) => (typeof r === 'string' ? r.toUpperCase() : String(r)))
        .includes('SUPER_ADMIN');

    const { accessManagerData, isAccessManagerLoading } = useGetAccessManager();
    const { tenantConfigData, isTenantConfigLoading, tenantConfigError } = useTenantConfig(selectedTenantId);

    if (!isSuperAdmin) {
        return <Navigate to='/dashboard' replace />;
    }

    const tenants = accessManagerData?.tenants ?? [];

    const tenantOptions: SelectOption[] = tenants.map((t) => ({
        value: t.id,
        label: t.name,
    }));

    const handleTenantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedTenantId(e.target.value);
    };

    const statusValue = tenantConfigData?.config?.status;
    const chipColor = statusChipColor(statusValue);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Tenant Config (read-only)</h1>
                <Select
                    options={tenantOptions}
                    value={selectedTenantId}
                    onChange={handleTenantChange}
                    placeholder='Select a tenant'
                    isLoading={isAccessManagerLoading}
                />
            </div>

            {selectedTenantId && (
                <>
                    {isTenantConfigLoading && <p className={styles.loading}>Loading config...</p>}

                    {tenantConfigError && (
                        <p className={styles.error}>Failed to load tenant configuration. Please try again.</p>
                    )}

                    {!isTenantConfigLoading && !tenantConfigError && tenantConfigData && (
                        <>
                            <div className={styles.tenantInfo}>
                                <h2 className={styles.tenantName}>{tenantConfigData.name}</h2>
                                {statusValue && (
                                    <Chip color={chipColor} variant='filled' size='sm'>
                                        {statusValue}
                                    </Chip>
                                )}
                                <span className={styles.timestamp}>Created: {tenantConfigData.created_at}</span>
                                <span className={styles.timestamp}>Updated: {tenantConfigData.updated_at}</span>
                            </div>

                            <div className={styles.configSections}>
                                {SECTION_KEYS.map(({ key, label }) => {
                                    const sectionData =
                                        key === 'status'
                                            ? tenantConfigData.config?.status
                                            : (tenantConfigData.config as Record<string, unknown>)?.[key];

                                    return (
                                        <Accordion key={key} header={label}>
                                            <SectionBody sectionKey={key} data={sectionData} />
                                        </Accordion>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default TenantConfigViewer;
